/**
 * OIDC ↔ app bridge.
 *
 * The api-client's token provider is `liveToken` from `session.ts`,
 * installed once in `main.tsx` so the first request already waits for
 * the stored session instead of leaving without a token. This component
 * keeps the session moving and the principal in step with it:
 *
 *   - in each token's last minute it renews the session, so requests
 *     rarely wait for a renewal;
 *   - when a user is loaded, removed, or expires unrenewed, the `me`
 *     query is invalidated, and its refetch renews or finds no session;
 *   - when another tab removes the shared user (signing out), this tab
 *     follows at once.
 *
 * A failed renewal keeps the current token; `session.ts` decides when the
 * session is really over.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';

import { qk } from '../lib/query-keys.js';
import { getUserManager, USER_KEY_PREFIX } from './oidc.js';
import { renewSession } from './session.js';

/** Wires the realm session's events to renewal and to the `me` query. */
export function OidcTokenBridge({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const manager = getUserManager();
    if (!manager) return;

    const refreshMe = (): void => void queryClient.invalidateQueries({ queryKey: qk.me() });
    const renew = (): void => void renewSession();
    function onStorage(event: StorageEvent): void {
      // A null key is localStorage.clear(); a removed user key is a sign-out elsewhere.
      const signedOut =
        event.key === null || (event.key.startsWith(USER_KEY_PREFIX) && event.newValue === null);
      if (signedOut) refreshMe();
    }

    manager.events.addUserLoaded(refreshMe);
    manager.events.addUserUnloaded(refreshMe);
    manager.events.addAccessTokenExpiring(renew);
    manager.events.addAccessTokenExpired(refreshMe);
    window.addEventListener('storage', onStorage);
    // Reading the stored user arms the expiring and expired timers above.
    void manager.getUser();
    return () => {
      manager.events.removeUserLoaded(refreshMe);
      manager.events.removeUserUnloaded(refreshMe);
      manager.events.removeAccessTokenExpiring(renew);
      manager.events.removeAccessTokenExpired(refreshMe);
      window.removeEventListener('storage', onStorage);
    };
  }, [queryClient]);

  return <>{children}</>;
}
